import {useCallback, useMemo} from 'react';
import {v4 as uuidv4} from 'uuid';
import {
    ResourceAllocationState,
    NonWorkingDay,
    PersonalAvailability,
    PersonalAvailabilityWeek
} from '../../../types';
import {
    generatePersonalAvailabilityWeeks,
    getWeekNumbersInRange
} from '../../../utils/availabilityCalculator';

type AuthenticatedFetch = (
    url: string,
    options?: RequestInit
) => Promise<Response>;

interface SignalRConnection {
    getConnectionId(): string | null;
}

interface ActionDeps {
    // Ref, not the state value: callbacks read state via stateRef.current so
    // their identities survive state changes — the provider value can then be
    // memoized and downstream memo boundaries hold.
    stateRef: {current: ResourceAllocationState};
    setState: React.Dispatch<React.SetStateAction<ResourceAllocationState>>;
    authenticatedFetch: AuthenticatedFetch;
    signalRService: SignalRConnection;
    customerId: string | undefined;
    baseUrl: string;
}

export const useAvailabilityActions = ({
    stateRef,
    setState,
    authenticatedFetch,
    signalRService,
    customerId,
    baseUrl
}: ActionDeps) => {
    // Non-working day operations
    const addNonWorkingDay = useCallback(
        (nonWorkingDay: Omit<NonWorkingDay, 'id'>): string => {
            const id = uuidv4();
            const newNonWorkingDay = {...nonWorkingDay, id};

            // Optimistic update
            setState(prev => ({
                ...prev,
                nonWorkingDays: {
                    ...prev.nonWorkingDays,
                    [id]: newNonWorkingDay
                }
            }));

            // API call
            if (customerId && authenticatedFetch) {
                const connectionId = signalRService.getConnectionId();

                authenticatedFetch(
                    `${baseUrl}/api/resource-allocation/non-working-days`,
                    {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'X-SignalR-Connection-Id': connectionId || ''
                        },
                        body: JSON.stringify({
                            id: newNonWorkingDay.id,
                            name: newNonWorkingDay.name,
                            startDate: newNonWorkingDay.startDate,
                            endDate: newNonWorkingDay.endDate,
                            hoursPerDay: newNonWorkingDay.hoursPerDay,
                            description: newNonWorkingDay.description
                        })
                    }
                )
                    .then(async (response: Response) => {
                        if (!response.ok) {
                            console.error(
                                '[addNonWorkingDay] API call failed:',
                                await response.text()
                            );
                            // Revert on failure
                            setState(prev => {
                                const {[id]: _removed, ...rest} =
                                    prev.nonWorkingDays;
                                return {...prev, nonWorkingDays: rest};
                            });
                        }
                    })
                    .catch((error: unknown) => {
                        console.error(
                            '[addNonWorkingDay] API call error:',
                            error
                        );
                        // Revert on error
                        setState(prev => {
                            const {[id]: _removed, ...rest} =
                                prev.nonWorkingDays;
                            return {...prev, nonWorkingDays: rest};
                        });
                    });
            }

            return id;
        },
        [
            setState,
            authenticatedFetch,
            signalRService,
            customerId,
            baseUrl
        ]
    );

    const updateNonWorkingDay = useCallback(
        (id: string, updates: Partial<NonWorkingDay>) => {
            const previous = stateRef.current.nonWorkingDays[id];

            // Optimistic update
            setState(prev => ({
                ...prev,
                nonWorkingDays: {
                    ...prev.nonWorkingDays,
                    [id]: {...prev.nonWorkingDays[id], ...updates}
                }
            }));

            // API call
            if (customerId && authenticatedFetch) {
                const connectionId = signalRService.getConnectionId();

                authenticatedFetch(
                    `${baseUrl}/api/resource-allocation/non-working-days/${id}`,
                    {
                        method: 'PUT',
                        headers: {
                            'Content-Type': 'application/json',
                            'X-SignalR-Connection-Id': connectionId || ''
                        },
                        body: JSON.stringify(updates)
                    }
                )
                    .then(async (response: Response) => {
                        if (!response.ok) {
                            console.error(
                                '[updateNonWorkingDay] API call failed:',
                                await response.text()
                            );
                            // Revert on failure
                            setState(prev => ({
                                ...prev,
                                nonWorkingDays: {
                                    ...prev.nonWorkingDays,
                                    [id]: previous
                                }
                            }));
                        }
                    })
                    .catch((error: unknown) => {
                        console.error(
                            '[updateNonWorkingDay] API call error:',
                            error
                        );
                        // Revert on error
                        setState(prev => ({
                            ...prev,
                            nonWorkingDays: {
                                ...prev.nonWorkingDays,
                                [id]: previous
                            }
                        }));
                    });
            }
        },
        [
            stateRef,
            setState,
            authenticatedFetch,
            signalRService,
            customerId,
            baseUrl
        ]
    );

    const deleteNonWorkingDay = useCallback(
        (id: string) => {
            const previous = stateRef.current.nonWorkingDays[id];

            // Optimistic update
            setState(prev => {
                const {[id]: _removed, ...rest} = prev.nonWorkingDays;
                return {...prev, nonWorkingDays: rest};
            });

            // API call
            if (customerId && authenticatedFetch) {
                const connectionId = signalRService.getConnectionId();

                authenticatedFetch(
                    `${baseUrl}/api/resource-allocation/non-working-days/${id}`,
                    {
                        method: 'DELETE',
                        headers: {
                            'X-SignalR-Connection-Id': connectionId || ''
                        }
                    }
                )
                    .then(async (response: Response) => {
                        if (!response.ok) {
                            console.error(
                                '[deleteNonWorkingDay] API call failed:',
                                await response.text()
                            );
                            // Revert on failure
                            setState(prev => ({
                                ...prev,
                                nonWorkingDays: {
                                    ...prev.nonWorkingDays,
                                    [id]: previous
                                }
                            }));
                        }
                    })
                    .catch((error: unknown) => {
                        console.error(
                            '[deleteNonWorkingDay] API call error:',
                            error
                        );
                        // Revert on error
                        setState(prev => ({
                            ...prev,
                            nonWorkingDays: {
                                ...prev.nonWorkingDays,
                                [id]: previous
                            }
                        }));
                    });
            }
        },
        [
            stateRef,
            setState,
            authenticatedFetch,
            signalRService,
            customerId,
            baseUrl
        ]
    );

    // Personal availability operations
    const addPersonalAvailability = useCallback(
        (availability: Omit<PersonalAvailability, 'id'>): string => {
            const state = stateRef.current;
            const id = uuidv4();
            const newAvailability: PersonalAvailability = {...availability, id};

            // Generate weeks locally
            const weekRecords = generatePersonalAvailabilityWeeks(
                newAvailability,
                state.workingHoursConfig.baseHoursPerWeek,
                state.workingHoursConfig.defaultHoursPerDay,
                'calculated'
            );

            // Prepare weeks with IDs (using current state for lookup)
            const weeksToSend = weekRecords.map(wr => {
                const existingWeekId = Object.keys(
                    state.personalAvailabilityWeeks
                ).find(
                    weekId =>
                        state.personalAvailabilityWeeks[weekId].personId ===
                            wr.personId &&
                        state.personalAvailabilityWeeks[weekId]
                            .globalWeekNumber === wr.globalWeekNumber
                );
                return {
                    ...wr,
                    id: existingWeekId || uuidv4()
                };
            });

            const previousWeeks = {...state.personalAvailabilityWeeks};

            setState(prev => {
                const newWeeks = {...prev.personalAvailabilityWeeks};

                weeksToSend.forEach(week => {
                    newWeeks[week.id] = week;
                });

                return {
                    ...prev,
                    personalAvailability: {
                        ...prev.personalAvailability,
                        [id]: newAvailability
                    },
                    personalAvailabilityWeeks: newWeeks
                };
            });

            // API call
            if (customerId && authenticatedFetch) {
                const connectionId = signalRService.getConnectionId();

                authenticatedFetch(
                    `${baseUrl}/api/resource-allocation/personal-availability`,
                    {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'X-SignalR-Connection-Id': connectionId || ''
                        },
                        body: JSON.stringify({
                            id: newAvailability.id,
                            personId: newAvailability.personId,
                            name: newAvailability.name,
                            description: newAvailability.description,
                            startDate: newAvailability.startDate,
                            endDate: newAvailability.endDate,
                            hoursPerDay: newAvailability.hoursPerDay,
                            recalculateWeeks: newAvailability.recalculateWeeks,
                            weeks: weeksToSend.map(w => ({
                                ...w,
                                isCalculated: w.source === 'calculated'
                            }))
                        })
                    }
                )
                    .then(async (response: Response) => {
                        if (!response.ok) {
                            console.error(
                                '[addPersonalAvailability] API call failed:',
                                await response.text()
                            );
                            // Revert on failure
                            setState(prev => {
                                const {[id]: _removed, ...rest} =
                                    prev.personalAvailability;
                                return {
                                    ...prev,
                                    personalAvailability: rest,
                                    personalAvailabilityWeeks: previousWeeks
                                };
                            });
                        }
                    })
                    .catch((error: unknown) => {
                        console.error(
                            '[addPersonalAvailability] API call error:',
                            error
                        );
                        setState(prev => {
                            const {[id]: _removed, ...rest} =
                                prev.personalAvailability;
                            return {
                                ...prev,
                                personalAvailability: rest,
                                personalAvailabilityWeeks: previousWeeks
                            };
                        });
                    });
            }

            return id;
        },
        [
            stateRef,
            setState,
            authenticatedFetch,
            signalRService,
            customerId,
            baseUrl
        ]
    );

    const updatePersonalAvailability = useCallback(
        (id: string, updates: Partial<PersonalAvailability>) => {
            const state = stateRef.current;
            const weeksToSend: PersonalAvailabilityWeek[] = [];
            const weekNumbersToDelete: number[] = [];

            setState(prev => {
                const existing = prev.personalAvailability[id];
                if (!existing) return prev;

                const updated: PersonalAvailability = {...existing, ...updates};

                // Get old and new affected week ranges
                const oldWeekNumbers = getWeekNumbersInRange(
                    new Date(existing.startDate),
                    new Date(existing.endDate)
                );
                const newWeekNumbers = getWeekNumbersInRange(
                    new Date(updated.startDate),
                    new Date(updated.endDate)
                );

                // Generate PersonalAvailabilityWeek records from updated period
                const weekRecords = generatePersonalAvailabilityWeeks(
                    updated,
                    prev.workingHoursConfig.baseHoursPerWeek,
                    prev.workingHoursConfig.defaultHoursPerDay,
                    'calculated'
                );

                const newWeeks = {...prev.personalAvailabilityWeeks};

                // Delete old weeks that are no longer in range (last edit wins - recalculate all)
                oldWeekNumbers.forEach(weekNum => {
                    if (!newWeekNumbers.includes(weekNum)) {
                        weekNumbersToDelete.push(weekNum);
                        const weekIdToDelete = Object.keys(newWeeks).find(
                            weekId =>
                                newWeeks[weekId].personId ===
                                    updated.personId &&
                                newWeeks[weekId].globalWeekNumber === weekNum
                        );
                        if (weekIdToDelete) {
                            delete newWeeks[weekIdToDelete];
                        }
                    }
                });

                // Update/create weeks in new range (last edit wins - overwrite all including manual)
                weekRecords.forEach(weekRecord => {
                    const existingWeekId = Object.keys(newWeeks).find(
                        weekId =>
                            newWeeks[weekId].personId === weekRecord.personId &&
                            newWeeks[weekId].globalWeekNumber ===
                                weekRecord.globalWeekNumber
                    );

                    if (existingWeekId) {
                        // Update existing
                        newWeeks[existingWeekId] = {
                            ...weekRecord,
                            id: existingWeekId
                        };
                    } else {
                        // Create new
                        const weekId = uuidv4();
                        newWeeks[weekId] = {
                            ...weekRecord,
                            id: weekId
                        };
                    }

                    // Add to list to send (using current ID)
                    weeksToSend.push(
                        newWeeks[existingWeekId || Object.keys(newWeeks).pop()!]
                    );
                });

                return {
                    ...prev,
                    personalAvailability: {
                        ...prev.personalAvailability,
                        [id]: updated
                    },
                    personalAvailabilityWeeks: newWeeks
                };
            });

            const previousPA = state.personalAvailability[id];
            const previousWeeks = {...state.personalAvailabilityWeeks};

            // API call
            if (customerId && authenticatedFetch) {
                const connectionId = signalRService.getConnectionId();

                authenticatedFetch(
                    `${baseUrl}/api/resource-allocation/personal-availability/${id}`,
                    {
                        method: 'PUT',
                        headers: {
                            'Content-Type': 'application/json',
                            'X-SignalR-Connection-Id': connectionId || ''
                        },
                        body: JSON.stringify({
                            id,
                            ...updates,
                            weeks: weeksToSend.map(w => ({
                                ...w,
                                isCalculated: w.source === 'calculated'
                            })),
                            weeksToDelete: weekNumbersToDelete
                        })
                    }
                )
                    .then(async (response: Response) => {
                        if (!response.ok) {
                            console.error(
                                '[updatePersonalAvailability] API call failed:',
                                await response.text()
                            );
                            // Revert
                            setState(prev => ({
                                ...prev,
                                personalAvailability: {
                                    ...prev.personalAvailability,
                                    [id]: previousPA
                                },
                                personalAvailabilityWeeks: previousWeeks
                            }));
                        }
                    })
                    .catch((error: unknown) => {
                        console.error(
                            '[updatePersonalAvailability] API call error:',
                            error
                        );
                        // Revert
                        setState(prev => ({
                            ...prev,
                            personalAvailability: {
                                ...prev.personalAvailability,
                                [id]: previousPA
                            },
                            personalAvailabilityWeeks: previousWeeks
                        }));
                    });
            }
        },
        [
            stateRef,
            setState,
            authenticatedFetch,
            signalRService,
            customerId,
            baseUrl
        ]
    );

    const deletePersonalAvailability = useCallback(
        (id: string) => {
            const state = stateRef.current;
            setState(prev => {
                const availability = prev.personalAvailability[id];
                if (!availability) return prev;

                // Get affected week range
                const weekNumbers = getWeekNumbersInRange(
                    new Date(availability.startDate),
                    new Date(availability.endDate)
                );

                // Recalculate affected weeks (last edit wins - remove all PersonalAvailabilityWeek records in range)
                const newWeeks = {...prev.personalAvailabilityWeeks};
                weekNumbers.forEach(weekNum => {
                    const weekIdToDelete = Object.keys(newWeeks).find(
                        weekId =>
                            newWeeks[weekId].personId ===
                                availability.personId &&
                            newWeeks[weekId].globalWeekNumber === weekNum
                    );
                    if (weekIdToDelete) {
                        delete newWeeks[weekIdToDelete];
                    }
                });

                const {[id]: _removed, ...rest} = prev.personalAvailability;
                return {
                    ...prev,
                    personalAvailability: rest,
                    personalAvailabilityWeeks: newWeeks
                };
            });

            const previousPA = state.personalAvailability[id];
            const previousWeeks = {...state.personalAvailabilityWeeks};

            // API call
            if (customerId && authenticatedFetch) {
                const connectionId = signalRService.getConnectionId();

                authenticatedFetch(
                    `${baseUrl}/api/resource-allocation/personal-availability/${id}`,
                    {
                        method: 'DELETE',
                        headers: {
                            'X-SignalR-Connection-Id': connectionId || ''
                        }
                    }
                )
                    .then(async (response: Response) => {
                        if (!response.ok) {
                            console.error(
                                '[deletePersonalAvailability] API call failed:',
                                await response.text()
                            );
                            // Revert
                            setState(prev => ({
                                ...prev,
                                personalAvailability: {
                                    ...prev.personalAvailability,
                                    [id]: previousPA
                                },
                                personalAvailabilityWeeks: previousWeeks
                            }));
                        }
                    })
                    .catch((error: unknown) => {
                        console.error(
                            '[deletePersonalAvailability] API call error:',
                            error
                        );
                        // Revert
                        setState(prev => ({
                            ...prev,
                            personalAvailability: {
                                ...prev.personalAvailability,
                                [id]: previousPA
                            },
                            personalAvailabilityWeeks: previousWeeks
                        }));
                    });
            }
        },
        [
            stateRef,
            setState,
            authenticatedFetch,
            signalRService,
            customerId,
            baseUrl
        ]
    );

    // Personal availability week operations
    const addPersonalAvailabilityWeek = useCallback(
        (week: Omit<PersonalAvailabilityWeek, 'id'>): string => {
            const id = uuidv4();
            const newWeek = {...week, id};

            // Optimistic update
            setState(prev => ({
                ...prev,
                personalAvailabilityWeeks: {
                    ...prev.personalAvailabilityWeeks,
                    [id]: newWeek
                }
            }));

            // API call
            if (customerId && authenticatedFetch) {
                const connectionId = signalRService.getConnectionId();

                authenticatedFetch(
                    `${baseUrl}/api/resource-allocation/personal-availability-weeks`,
                    {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'X-SignalR-Connection-Id': connectionId || ''
                        },
                        body: JSON.stringify({
                            id,
                            personalAvailabilityId: week.personalAvailabilityId,
                            personId: week.personId,
                            globalWeekNumber: week.globalWeekNumber,
                            availableHours: week.availableHours
                        })
                    }
                )
                    .then(async (response: Response) => {
                        if (!response.ok) {
                            console.error(
                                '[addPersonalAvailabilityWeek] API call failed:',
                                await response.text()
                            );
                            // Revert
                            setState(prev => {
                                const {[id]: _removed, ...rest} =
                                    prev.personalAvailabilityWeeks;
                                return {
                                    ...prev,
                                    personalAvailabilityWeeks: rest
                                };
                            });
                        }
                    })
                    .catch((error: unknown) => {
                        console.error(
                            '[addPersonalAvailabilityWeek] API call error:',
                            error
                        );
                        setState(prev => {
                            const {[id]: _removed, ...rest} =
                                prev.personalAvailabilityWeeks;
                            return {...prev, personalAvailabilityWeeks: rest};
                        });
                    });
            }

            return id;
        },
        [
            setState,
            authenticatedFetch,
            signalRService,
            customerId,
            baseUrl
        ]
    );

    const updatePersonalAvailabilityWeek = useCallback(
        (id: string, updates: Partial<PersonalAvailabilityWeek>) => {
            const previousWeek = stateRef.current.personalAvailabilityWeeks[id];

            // Optimistic update
            setState(prev => ({
                ...prev,
                personalAvailabilityWeeks: {
                    ...prev.personalAvailabilityWeeks,
                    [id]: {...prev.personalAvailabilityWeeks[id], ...updates}
                }
            }));

            // API call
            if (customerId && authenticatedFetch) {
                const connectionId = signalRService.getConnectionId();

                authenticatedFetch(
                    `${baseUrl}/api/resource-allocation/personal-availability-weeks/${id}`,
                    {
                        method: 'PUT',
                        headers: {
                            'Content-Type': 'application/json',
                            'X-SignalR-Connection-Id': connectionId || ''
                        },
                        body: JSON.stringify(updates)
                    }
                )
                    .then(async (response: Response) => {
                        if (!response.ok) {
                            console.error(
                                '[updatePersonalAvailabilityWeek] API call failed:',
                                await response.text()
                            );
                            // Revert
                            setState(prev => ({
                                ...prev,
                                personalAvailabilityWeeks: {
                                    ...prev.personalAvailabilityWeeks,
                                    [id]: previousWeek
                                }
                            }));
                        }
                    })
                    .catch((error: unknown) => {
                        console.error(
                            '[updatePersonalAvailabilityWeek] API call error:',
                            error
                        );
                        setState(prev => ({
                            ...prev,
                            personalAvailabilityWeeks: {
                                ...prev.personalAvailabilityWeeks,
                                [id]: previousWeek
                            }
                        }));
                    });
            }
        },
        [
            stateRef,
            setState,
            authenticatedFetch,
            signalRService,
            customerId,
            baseUrl
        ]
    );

    const deletePersonalAvailabilityWeek = useCallback(
        (id: string) => {
            const previousWeek = stateRef.current.personalAvailabilityWeeks[id];

            // Optimistic update
            setState(prev => {
                const {[id]: _removed, ...rest} = prev.personalAvailabilityWeeks;
                return {...prev, personalAvailabilityWeeks: rest};
            });

            // API call
            if (customerId && authenticatedFetch) {
                const connectionId = signalRService.getConnectionId();

                authenticatedFetch(
                    `${baseUrl}/api/resource-allocation/personal-availability-weeks/${id}`,
                    {
                        method: 'DELETE',
                        headers: {
                            'X-SignalR-Connection-Id': connectionId || ''
                        }
                    }
                )
                    .then(async (response: Response) => {
                        if (!response.ok) {
                            console.error(
                                '[deletePersonalAvailabilityWeek] API call failed:',
                                await response.text()
                            );
                            // Revert
                            setState(prev => ({
                                ...prev,
                                personalAvailabilityWeeks: {
                                    ...prev.personalAvailabilityWeeks,
                                    [id]: previousWeek
                                }
                            }));
                        }
                    })
                    .catch((error: unknown) => {
                        console.error(
                            '[deletePersonalAvailabilityWeek] API call error:',
                            error
                        );
                        setState(prev => ({
                            ...prev,
                            personalAvailabilityWeeks: {
                                ...prev.personalAvailabilityWeeks,
                                [id]: previousWeek
                            }
                        }));
                    });
            }
        },
        [
            stateRef,
            setState,
            authenticatedFetch,
            signalRService,
            customerId,
            baseUrl
        ]
    );

    const deletePersonalAvailabilityWeeksInRange = useCallback(
        (personId: string, weekNumbers: number[]) => {
            const state = stateRef.current;
            const previousWeeks = {...state.personalAvailabilityWeeks};

            // Optimistic update
            setState(prev => {
                const newWeeks = {...prev.personalAvailabilityWeeks};
                Object.keys(newWeeks).forEach(weekId => {
                    const week = newWeeks[weekId];
                    if (
                        week.personId === personId &&
                        weekNumbers.includes(week.globalWeekNumber)
                    ) {
                        delete newWeeks[weekId];
                    }
                });
                return {...prev, personalAvailabilityWeeks: newWeeks};
            });

            // API call
            if (customerId && authenticatedFetch) {
                const connectionId = signalRService.getConnectionId();

                authenticatedFetch(
                    `${baseUrl}/api/resource-allocation/personal-availability-weeks/delete-range`,
                    {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'X-SignalR-Connection-Id': connectionId || ''
                        },
                        body: JSON.stringify({
                            personId,
                            weekNumbers
                        })
                    }
                )
                    .then(async (response: Response) => {
                        if (!response.ok) {
                            console.error(
                                '[deletePersonalAvailabilityWeeksInRange] API call failed:',
                                await response.text()
                            );
                            // Revert
                            setState(prev => ({
                                ...prev,
                                personalAvailabilityWeeks: previousWeeks
                            }));
                        }
                    })
                    .catch((error: unknown) => {
                        console.error(
                            '[deletePersonalAvailabilityWeeksInRange] API call error:',
                            error
                        );
                        setState(prev => ({
                            ...prev,
                            personalAvailabilityWeeks: previousWeeks
                        }));
                    });
            }
        },
        [
            stateRef,
            setState,
            authenticatedFetch,
            signalRService,
            customerId,
            baseUrl
        ]
    );

    // Fold availability fetched outside /state (a resolved person's page) into
    // the maps. Add-only, like ensurePeople: never clobbers optimistic edits or
    // resurrects optimistic deletions.
    const ensurePersonalAvailability = useCallback(
        (
            records: PersonalAvailability[],
            weeks: PersonalAvailabilityWeek[]
        ) => {
            setState(prev => {
                const newRecords = records.filter(
                    r => r && !prev.personalAvailability[r.id]
                );
                const newWeeks = weeks.filter(
                    w => w && !prev.personalAvailabilityWeeks[w.id]
                );
                if (newRecords.length === 0 && newWeeks.length === 0) {
                    return prev;
                }
                const mergedRecords = {...prev.personalAvailability};
                newRecords.forEach(r => {
                    mergedRecords[r.id] = r;
                });
                const mergedWeeks = {...prev.personalAvailabilityWeeks};
                newWeeks.forEach(w => {
                    mergedWeeks[w.id] = w;
                });
                return {
                    ...prev,
                    personalAvailability: mergedRecords,
                    personalAvailabilityWeeks: mergedWeeks
                };
            });
        },
        [setState]
    );

    return useMemo(
        () => ({
            addNonWorkingDay,
            updateNonWorkingDay,
            deleteNonWorkingDay,
            ensurePersonalAvailability,
            addPersonalAvailability,
            updatePersonalAvailability,
            deletePersonalAvailability,
            addPersonalAvailabilityWeek,
            updatePersonalAvailabilityWeek,
            deletePersonalAvailabilityWeek,
            deletePersonalAvailabilityWeeksInRange
        }),
        [
            addNonWorkingDay,
            updateNonWorkingDay,
            deleteNonWorkingDay,
            ensurePersonalAvailability,
            addPersonalAvailability,
            updatePersonalAvailability,
            deletePersonalAvailability,
            addPersonalAvailabilityWeek,
            updatePersonalAvailabilityWeek,
            deletePersonalAvailabilityWeek,
            deletePersonalAvailabilityWeeksInRange
        ]
    );
};
